import React, { useMemo, useState } from 'react';

import { useMenuState } from '@szhsin/react-menu';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import EmbedYoutube from 'components/embedYoutubeModal';
import { OPTION_COMBOS_STRIKE_GAPS, SPREADS_YOUTUBE_VIDEO } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { TIME_FORMATS } from 'constants/timeformats';
import { noop } from 'helpers';
import { dateFormat, DateType, getDateTime } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { useOptionsCombosFilterStrip } from 'hooks';
import { NewPlayIcon } from 'styles/assets/icons';
import { Product } from 'types/IProducts';
import { Box, IconButton, InputBase } from 'UIKit';
import DeltaButton from 'UIKit/DeltaButton/DeltaButton';
import FilterDropDown, { Option } from 'UIKit/FilterDropDown/FilterDropDown';

import styles from './combosFilterStrip.module.scss';
import dropdownStyles from './expiryDropdown.module.scss';

type Props = {
  productList: Product[];
  setActiveProducts: (products: Product[]) => void;
  searchTerm: string;
};

const AllExpiry = 'All Expiry';

const OptionsCombosFilterStrip = ({
  productList,
  setActiveProducts,
  searchTerm,
}: Props) => {
  const { t } = useTranslation(['markets', 'common']);
  const [expiryFilterMenuState, expiryFilterToggleMenu] = useMenuState();
  const [strikeGapFilterMenuState, strikeGapFilterToggleMenu] = useMenuState();

  const {
    underlyingAssets,
    selectedAsset,
    setSelectedAsset,
    expiryDates,
    selectedExpiry,
    selectedStrikeGaps,
    setSelectedExpiry,
    selectedComboType,
    setSelectedComboType,
    selectedFromStrike,
    setSelectedFromStrike,
    selectedToStrike,
    setSelectedToStrike,
    updateSelectedStrikeGaps,
  } = useOptionsCombosFilterStrip({
    productList,
    setActiveProducts,
    searchTerm,
  });

  const showAssetButtons = underlyingAssets.length > 1;

  const comboTypesList = ['all', 'call_spread', 'put_spread'];

  const handleFilterComboClick = (comboType: string) => {
    setSelectedComboType(comboType);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.COMBOS_FILTER_CLICK, {
      Type: comboType,
    });
  };

  const handleEventExpiryClick = (date: string | DateType) => {
    setSelectedExpiry(date);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.COMBOS_FILTER_CLICK, {
      Type: 'Expiry',
    });
  };

  const getDropdownOptionLabel = (date: DateType) =>
    date === AllExpiry ? date : dateFormat(getDateTime(date), TIME_FORMATS.D_MMM_YY);

  const filterOptions: Option[] = expiryDates.map(date => {
    return {
      id: date,
      label: getDropdownOptionLabel(date),
      checked: date === selectedExpiry,
      checkbox: false,
      onClick: () => handleEventExpiryClick(date),
    };
  });

  const handleEventStrikeInput = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.COMBOS_FILTER_CLICK, {
      Type: 'Strike',
    });
  };

  const handleStrikGapOnClick = ({ selectedValue }) => {
    updateSelectedStrikeGaps({ selectedValue });
    if (!selectedStrikeGaps[selectedValue]) {
      trackMixpanelEvent('All markets page - combos filter clicked', {
        type: 'Strike Gap',
        value: selectedValue,
      });
    }
  };

  const strikeGapOptions: Option[] = [
    {
      id: `All - Strike Gap`,
      label: t('common:all'),
      onClick: () => handleStrikGapOnClick({ selectedValue: 'all' }),
      checked: selectedStrikeGaps.all,
    },
  ].concat(
    OPTION_COMBOS_STRIKE_GAPS.map(strikeGap => {
      const strikeGapString = String(strikeGap);
      return {
        id: strikeGapString,
        label: strikeGapString,
        checked: Boolean(selectedStrikeGaps[strikeGap]),
        checkbox: true,
        onClick: () => handleStrikGapOnClick({ selectedValue: strikeGap }),
      };
    })
  );

  const strikeGapSelectionText = useMemo(() => {
    if (selectedStrikeGaps.all) return undefined;

    return Object.keys(selectedStrikeGaps)
      .filter(strikeGapKey => selectedStrikeGaps[strikeGapKey])
      .join(', ');
  }, [selectedStrikeGaps]);

  const strikeGapLabel = selectedStrikeGaps.all ? t('common:all') : '';

  return (
    (<Box flex="flex" alignItems="center" className={styles.combosFilterContainer}>
      {showAssetButtons
        ? underlyingAssets.map((asset: string) => (
            <DeltaButton
              useBasicButton
              data-testid="options-combos-asset-filter-button"
              onClick={() => setSelectedAsset(asset)}
              key={asset}
              className={cn(styles.combosFilterButton, {
                [styles.active]: selectedAsset === asset,
              })}
              text={asset}
            />
          ))
        : null}
      {showAssetButtons ? <div className={styles.verticalLine} data-palette="OptionsCombosFilterStrip" /> : null}
      <Box flex="flex" alignItems="center" className={styles.comboTypesContainer}>
        {comboTypesList.map((comboType: string) => (
          <DeltaButton
            useBasicButton
            data-testid="options-combos-comboType-filter-button"
            onClick={() => handleFilterComboClick(comboType)}
            key={comboType}
            className={cn(styles.combosFilterButton, {
              [styles.active]: selectedComboType === comboType,
            })}
            text={t(`markets:comboTypeFilter.${comboType}`)}
          />
        ))}
      </Box>
      <div className={styles.verticalLine} />
      <FilterDropDown
        menuState={expiryFilterMenuState}
        toggleMenu={expiryFilterToggleMenu}
        label={getDropdownOptionLabel(selectedExpiry)}
        options={filterOptions}
        closeOnSelect
        testId="spreads-expiry-filter-dropdown"
        styleOverrideClasses={{
          button: dropdownStyles.button,
          labelSection: dropdownStyles.labelSection,
          dropDown: dropdownStyles.dropDown,
          dropdownOption: dropdownStyles.dropdownOption,
          optionLabel: dropdownStyles.optionLabel,
          headerOption: dropdownStyles.headerOption,
        }}
        className={dropdownStyles.parent}
      />
      <div className={styles.verticalLine} />
      <Box flex="flex" alignItems="center" className={styles.strikeRangeContainer}>
        <Box as="span" className={styles.strikeRangeLabel}>
          {t('markets:strikeRange')}
        </Box>
        <Box flex="flex" alignItems="center" className={styles.strikeInputContainer}>
          {/* FROM STRIKE PRICE INPUT */}
          <InputBase
            className={styles.strikeInput}
            value={selectedFromStrike ?? ''}
            data-testid="options-combos-strike-filter-input"
            placeholder="From"
            onClick={handleEventStrikeInput}
            onChange={e => {
              setSelectedFromStrike((e.target as HTMLInputElement).valueAsNumber);
            }}
            type="number"
            aria-label="From"
          />

          {/* TO STRIKE PRICE INPUT */}
          <InputBase
            className={styles.strikeInput}
            value={selectedToStrike ?? ''}
            data-testid="options-combos-strike-filter-input"
            placeholder="To"
            onChange={e => {
              setSelectedToStrike((e.target as HTMLInputElement).valueAsNumber);
            }}
            onClick={handleEventStrikeInput}
            type="number"
            aria-label="To"
          />
        </Box>
      </Box>
      <Box className={styles.verticalLine} />
      <Box className={styles.strikeGap} flex="flex" alignItems="center">
        <Box as="span" className={styles.strikeGapLabel}>
          {t('markets:strikeGap')}
        </Box>
        <FilterDropDown
          menuState={strikeGapFilterMenuState}
          toggleMenu={strikeGapFilterToggleMenu}
          label={strikeGapLabel}
          options={strikeGapOptions}
          testId="strike-gap"
          closeOnSelect={false}
          className={styles.strikeGapOptions}
          selectionText={strikeGapSelectionText}
          styleOverrideClasses={{
            button: styles.strikeGapOptionsDropDownButton,
            labelSection: styles.strikeGapOptionsDropDownLabelSection,
            arrowUp: styles.strikeGapOptionsDropDownArrowUp,
            arrowDown: styles.strikeGapOptionsDropDownArrowDown,
          }}
        />
      </Box>
    </Box>)
  );
};

const MobileOptionsCombosFilterStrip = ({
  productList,
  setActiveProducts,
  searchTerm,
}: Props) => {
  const { t } = useTranslation(['markets']);
  const [expiryFilterMenuState, expiryFilterToggleMenu] = useMenuState();
  const [strikeGapFilterMenuState, strikeGapFilterToggleMenu] = useMenuState();
  const [playVideo, setPlayVideo] = useState(false);

  const {
    underlyingAssets,
    selectedAsset,
    setSelectedAsset,
    expiryDates,
    selectedExpiry,
    selectedStrikeGaps,
    setSelectedExpiry,
    selectedComboType,
    setSelectedComboType,
    selectedFromStrike,
    setSelectedFromStrike,
    selectedToStrike,
    setSelectedToStrike,
    updateSelectedStrikeGaps,
  } = useOptionsCombosFilterStrip({
    productList,
    setActiveProducts,
    searchTerm,
  });

  const showAssetButtons = underlyingAssets.length > 1;

  const comboTypesList = ['all', 'call_spread', 'put_spread'];

  const handleFilterComboClick = (comboType: string) => {
    setSelectedComboType(comboType);
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.COMBOS_FILTER_CLICK, {
      Type: comboType,
    });
  };

  const handleEventStrikeInput = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MARKETS.COMBOS_FILTER_CLICK, {
      Type: 'Strike',
    });
  };

  const getDropdownOptionLabel = (date: DateType) =>
    date === AllExpiry ? date : dateFormat(getDateTime(date), TIME_FORMATS.D_MMM_YY);

  const filterOptions: Option[] = expiryDates.map(date => {
    return {
      id: date,
      label: getDropdownOptionLabel(date),
      checked: date === selectedExpiry,
      checkbox: false,
      onClick: () => setSelectedExpiry(date),
    };
  });

  const handleStrikGapOnClick = ({ selectedValue }) => {
    updateSelectedStrikeGaps({ selectedValue });
    if (!selectedStrikeGaps[selectedValue]) {
      trackMixpanelEvent('All markets page - combos filter clicked', {
        type: 'Strike Gap',
        value: selectedValue,
      });
    }
  };

  const strikeGapOptions: Option[] = [
    {
      id: `All - Strike Gap`,
      label: t('common:all'),
      onClick: () => handleStrikGapOnClick({ selectedValue: 'all' }),
      checked: selectedStrikeGaps.all,
    },
  ].concat(
    OPTION_COMBOS_STRIKE_GAPS.map(strikeGap => {
      const strikeGapString = String(strikeGap);
      return {
        id: strikeGapString,
        label: strikeGapString,
        checked: Boolean(selectedStrikeGaps[strikeGap]),
        checkbox: true,
        onClick: () => handleStrikGapOnClick({ selectedValue: strikeGap }),
      };
    })
  );

  const strikeGapSelectionText = useMemo(() => {
    if (selectedStrikeGaps.all) return undefined;

    return Object.keys(selectedStrikeGaps)
      .filter(strikeGapKey => selectedStrikeGaps[strikeGapKey])
      .join(', ');
  }, [selectedStrikeGaps]);

  const strikeGapLabel = selectedStrikeGaps.all ? t('common:all') : '';

  return (
    (<Box flex="flex" flexDir="column" className={styles.mobileCombosFilterContainer}>
      <Box
        flex="flex"
        flexDir="row"
        alignItems="center"
        justifyContent="between"
        wrap="wrap"
        className={`ms-3 me-3 ${styles.mobileCombosFilterUpperContainer}`}
      >
        {showAssetButtons
          ? underlyingAssets.map((asset: string) => (
              <DeltaButton
                useBasicButton
                data-testid="options-combos-asset-filter-button"
                data-data-testid="options-combos-asset-filter-button"
                testId="options-combos-asset-filter-button"
                onClick={() => setSelectedAsset(asset)}
                key={asset}
                className={cn(styles.combosFilterButton, {
                  [styles.active]: selectedAsset === asset,
                })}
                text={asset}
              />
            ))
          : null}

        {showAssetButtons ? <div
          className={styles.verticalLine}
          data-palette="MobileOptionsCombosFilterStrip" /> : null}

        <Box flex="flex" alignItems="center" className={styles.comboTypesContainer}>
          {comboTypesList.map((comboType: string) => (
            <DeltaButton
              useBasicButton
              data-testid="options-combos-comboType-filter-button"
              data-data-testid="options-combos-comboType-filter-button"
              testId="options-combos-comboType-filter-button"
              onClick={() => handleFilterComboClick(comboType)}
              key={comboType}
              className={cn(styles.combosFilterButton, {
                [styles.active]: selectedComboType === comboType,
              })}
              text={t(`markets:comboTypeFilter.${comboType}`)}
            />
          ))}
        </Box>

        <FilterDropDown
          menuState={expiryFilterMenuState}
          toggleMenu={expiryFilterToggleMenu}
          label={getDropdownOptionLabel(selectedExpiry)}
          options={filterOptions}
          closeOnSelect
          testId="spreads-expiry-filter-dropdown"
          styleOverrideClasses={{
            button: dropdownStyles.button,
            labelSection: dropdownStyles.labelSection,
            dropDown: dropdownStyles.dropDownMobile,
            dropdownOption: dropdownStyles.dropdownOption,
            optionLabel: dropdownStyles.optionLabel,
            headerOption: dropdownStyles.headerOption,
          }}
          className={`${dropdownStyles.parent} ${styles.expiryDateButton}`}
        />
      </Box>
      <Box
        flex="flex"
        alignItems="center"
        justifyContent="between"
        className={`ms-3 me-3 ${styles.strikeRangeGapContainer}`}
      >
        <Box flex="flex" flexDir="column" className={styles.strikeRangeContainer}>
          <Box as="span" className={styles.strikeRangeLabel}>
            {t('markets:strikeRange')}
          </Box>
          <Box flex="flex" alignItems="center" className={styles.strikeInputContainer}>
            {/* FROM STRIKE PRICE INPUT */}
            <InputBase
              className={styles.strikeInput}
              value={selectedFromStrike ?? ''}
              data-testid="options-combos-strike-filter-input"
              placeholder="From"
              onClick={handleEventStrikeInput}
              onChange={e =>
                setSelectedFromStrike((e.target as HTMLInputElement).valueAsNumber)
              }
              type="number"
              aria-label="From"
            />

            {/* TO STRIKE PRICE INPUT */}
            <InputBase
              className={styles.strikeInput}
              value={selectedToStrike ?? ''}
              data-testid="options-combos-strike-filter-input"
              placeholder="To"
              onClick={handleEventStrikeInput}
              onChange={e =>
                setSelectedToStrike((e.target as HTMLInputElement).valueAsNumber)
              }
              type="number"
              aria-label="To"
            />
          </Box>
        </Box>

        <Box className={styles.strikeGap} flex="flex" flexDir="column">
          <Box as="span" className={styles.strikeGapLabel}>
            {t('markets:strikeGap')}
          </Box>
          <Box>
            <FilterDropDown
              menuState={strikeGapFilterMenuState}
              toggleMenu={strikeGapFilterToggleMenu}
              label={strikeGapLabel}
              options={strikeGapOptions}
              closeOnSelect={false}
              testId="strike-gap"
              className={styles.strikeGapOptions}
              selectionText={strikeGapSelectionText}
              styleOverrideClasses={{
                button: styles.strikeGapOptionsDropDownButton,
                labelSection: styles.strikeGapOptionsDropDownLabelSection,
                arrowUp: styles.strikeGapOptionsDropDownArrowUp,
                arrowDown: styles.strikeGapOptionsDropDownArrowDown,
              }}
            />
          </Box>
        </Box>
        <IconButton
          onKeyDown={noop}
          data-testid="Spreads-play-video-btn-mobile"
          className={styles.playIcon}
          onClick={() => setPlayVideo(!playVideo)}
          icon={<NewPlayIcon className="playIcon" />}
        />
        <EmbedYoutube
          src={SPREADS_YOUTUBE_VIDEO}
          isOpen={playVideo}
          onClose={() => setPlayVideo(false)}
          autoPlay={false}
        />
      </Box>
    </Box>)
  );
};

export default OptionsCombosFilterStrip;
export { MobileOptionsCombosFilterStrip };
